// src/pages/MrpProducts.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { Info } from "lucide-react";
import UserAccessSummary from "../components/layout/UserAccessSummary";
import { filterRowsByAccess, getUserAccessInfo } from "../utils/accessFilters";
import useAccessOverrideVersion from "../hooks/useAccessOverrideVersion";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";
const ILS_UPDATE_ENDPOINT = "/api/mrp/products/ils-update";

const STORAGE_DIRTY_KEY = "pp_unsaved_mrp_products";
const STORAGE_WIDTHS_KEY = "mrp.products.widths";
const CHECKBOX_COLUMN_WIDTH = 44;
const COMMODITY_OPTIONS = [
  { value: "Furniture", label: "Furniture" },
  { value: "Appliances", label: "Appliances" },
  { value: "Housewares", label: "Housewares" },
  { value: "Sporting Goods", label: "Sporting Goods" },
  { value: "Luggage", label: "Luggage" },
];

const COLUMN_CONFIG = [
  { key: "cust_code", label: "Cust Code", width: 200, type: "select", placeholder: "-- Select Cust Code --" },
  { key: "retail_sku", label: "Retail SKU", width: 160, type: "text" },
  { key: "product_code", label: "Product Code", width: 180, type: "text" },
  { key: "ils_update_enabled", label: "iLS Update", width: 120, type: "readonly" },
  { key: "item_name", label: "Item Name", width: 220, type: "text" },
  { key: "item_cn_name", label: "Item Name CN", width: 220, type: "text" },
  { key: "commodity", label: "Commodity", width: 160, type: "select", placeholder: "-- Select Commodity --" },
  { key: "supplier", label: "Supplier", width: 140, type: "text" },
  { key: "supplier_code", label: "Supplier Code", width: 160, type: "text" },
  { key: "weight_lbs", label: "Wt-lbs", width: 120, type: "number" },
  { key: "length_in", label: "L-in", width: 120, type: "number" },
  { key: "width_in", label: "W-in", width: 120, type: "number" },
  { key: "height_in", label: "H-in", width: 120, type: "number" },
  { key: "cft", label: "CFT", width: 120, type: "number" },
  { key: "weight_kgs", label: "Wt-kgs", width: 120, type: "number" },
  { key: "dimension_cm", label: "Dim-cm", width: 160, type: "text" },
  { key: "pcs_per_box", label: "Pcs/Box", width: 120, type: "number" },
  { key: "boxes_per_plt", label: "Box/Plt", width: 120, type: "number" },
  { key: "plt_cube", label: "Plt Cube", width: 120, type: "number" },
];

const COLUMN_KEYS = COLUMN_CONFIG.map((col) => col.key);

const TEXT_COLUMNS = [
  "cust_code",
  "retail_sku",
  "product_code",
  "ils_update_enabled",
  "item_name",
  "item_cn_name",
  "commodity",
  "supplier",
  "supplier_code",
  "dimension_cm",
];
const MULTI_FILTER_KEYS = new Set(TEXT_COLUMNS);
const NUMBER_COLUMNS = [
  "weight_lbs",
  "length_in",
  "width_in",
  "height_in",
  "cft",
  "weight_kgs",
  "pcs_per_box",
  "boxes_per_plt",
  "plt_cube",
];
const NUMBER_COLUMN_SET = new Set(NUMBER_COLUMNS);
const PAGE_OPTIONS = [50, 100, 200, "All"];
const EMPTY_VALUE = "__EMPTY__";

const createInitialFilters = () => {
  const next = {};
  TEXT_COLUMNS.forEach((key) => {
    next[key] = [];
  });
  NUMBER_COLUMNS.forEach((key) => {
    next[key] = { operator: "", value: "" };
  });
  return next;
};

const EMPTY_ROW = {
  cust_code: "",
  retail_sku: "",
  product_code: "",
  ils_update_enabled: true,
  item_name: "",
  item_cn_name: "",
  commodity: "",
  supplier: "",
  supplier_code: "",
  weight_lbs: "",
  length_in: "",
  width_in: "",
  height_in: "",
  cft: "",
  weight_kgs: "",
  dimension_cm: "",
  pcs_per_box: "",
  boxes_per_plt: "",
  plt_cube: "",
};

const buildCompositeKey = (cust, product, supplier) => {
  const c = String(cust || "").trim();
  const p = String(product || "").trim();
  const s = String(supplier || "").trim();
  return c && p ? `${c}||${p}||${s}` : "";
};

const withDefaultFields = (rows = []) =>
  rows.map((row) => ({ ...EMPTY_ROW, ...(row || {}) }));

const ensureRowIds = (rows = []) =>
  rows.map((row) => {
    if (row && row._localId) return row;
    return { ...row, _localId: genId() };
  });

const genId = () => `tmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const rowsSnapshot = (rows = []) => {
  const serialised = rows.map(({ _localId, ...rest }) => ({ ...rest }));
  return JSON.stringify(serialised);
};

const mirrorDirtyToStorage = (flag) => {
  try {
    if (flag) localStorage.setItem(STORAGE_DIRTY_KEY, "1");
    else localStorage.removeItem(STORAGE_DIRTY_KEY);
  } catch {
    /* ignore */
  }
};

const parseNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = Number(value);
  return Number.isFinite(num) ? num : "";
};

const getProductId = (row) => {
  if (!row) return null;
  if (row.id !== null && row.id !== undefined) return row.id;
  if (row.product_id !== null && row.product_id !== undefined) return row.product_id;
  if (row.productId !== null && row.productId !== undefined) return row.productId;
  return null;
};

const normalizeHeaderLabel = (label) =>
  String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const buildCustomerMetadata = (items = []) => {
  const options = [];
  const aliasMap = {};
  const primaryMap = {};
  items.forEach((item) => {
    if (!item || typeof item !== "object") return;
    const primary = String(item.cust_code ?? item.code ?? "").trim();
    const alias = String(item.wms_code ?? "").trim();
    if (!primary) return;
    const lower = primary.toLowerCase();
    if (!primaryMap[lower]) {
      options.push({
        value: primary,
        label: primary,
      });
      primaryMap[lower] = primary;
    }
    if (alias) {
      const aliasLower = alias.toLowerCase();
      if (!aliasMap[aliasLower]) aliasMap[aliasLower] = primary;
    }
  });
  options.sort((a, b) => a.value.localeCompare(b.value));
  return { options, aliasMap, primaryMap };
};

export default function MrpProducts() {
  const accessVersion = useAccessOverrideVersion();
  const [rows, setRows] = useState([]);
  const [savedSnapshot, setSavedSnapshot] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncSummary, setSyncSummary] = useState(null);
  const [syncError, setSyncError] = useState("");
  const [lastSyncAt, setLastSyncAt] = useState("");
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [duplicateIds, setDuplicateIds] = useState(() => new Set());
  const [highlightCells, setHighlightCells] = useState(() => new Map());
  const [filters, setFilters] = useState(() => createInitialFilters());
  const [filterOptions, setFilterOptions] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortKey, setSortKey] = useState("");
  const [sortDirection, setSortDirection] = useState(0);
  const [widths, setWidths] = useState(() => {
    try {
      const cached = localStorage.getItem(STORAGE_WIDTHS_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return { ...parsed };
      }
    } catch {
      /* ignore */
    }
    return COLUMN_CONFIG.reduce((acc, col) => {
      acc[col.key] = col.width;
      return acc;
    }, {});
  });
  const [customerOptions, setCustomerOptions] = useState([]);
  const aliasMapRef = useRef({});
  const primaryMapRef = useRef({});
  const me = getCurrentUser();
  const isAdmin = (me?.role || "").toLowerCase() === "admin";

  const dragRef = useRef({ key: "", startX: 0, startW: 0 });
  const { activeCustTokens, restrictCust } = getUserAccessInfo();
  const accessFilteredRows = useMemo(() => {
    void accessVersion;
    return filterRowsByAccess(rows, {
      columns: COLUMN_KEYS,
      custKeys: ["cust_code"],
      allowBlank: true,
    });
  }, [rows, accessVersion]);
  const visibleColumns = useMemo(
    () =>
      COLUMN_CONFIG.map((col) => {
        const width = widths[col.key] || col.width;
        if (col.key === "cust_code") {
          return { ...col, width, options: customerOptions };
        }
        if (col.key === "commodity") {
          return { ...col, width, options: COMMODITY_OPTIONS };
        }
        return { ...col, width };
      }),
    [widths, customerOptions]
  );

  const normalizeCustCode = useCallback((code, { strict = false } = {}) => {
    const trimmed = String(code ?? "").trim();
    if (!trimmed) return "";
    const lower = trimmed.toLowerCase();
    const primaryMap = primaryMapRef.current || {};
    if (primaryMap[lower]) return primaryMap[lower];
    const aliasMap = aliasMapRef.current || {};
    const primaryFromAlias = aliasMap[lower];
    if (primaryFromAlias) {
      const canonical = primaryMap[primaryFromAlias.toLowerCase()] || primaryFromAlias;
      return canonical;
    }
    return strict ? "" : trimmed;
  }, []);

  const computeDuplicateIds = useCallback(
    (list) => {
      const map = new Map();
      const missing = new Set();
      list.forEach((row) => {
        const cust = normalizeCustCode(row.cust_code, { strict: true });
        const prod = String(row.product_code ?? "").trim();
        if (!cust || !prod) {
          missing.add(row._localId);
          return;
        }
        const supplier = String(row.supplier_code ?? "").trim();
        const key = buildCompositeKey(cust, prod, supplier);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(row._localId);
      });
      const duplicates = new Set();
      map.forEach((ids) => {
        if (ids.length > 1) ids.forEach((id) => duplicates.add(id));
      });
      missing.forEach((id) => duplicates.add(id));
      return duplicates;
    },
    [normalizeCustCode],
  );
  const refreshDuplicates = useCallback(
    (list) => {
      void accessVersion;
      const visible = restrictCust
        ? filterRowsByAccess(list, { columns: COLUMN_KEYS, custKeys: ["cust_code"], allowBlank: true })
        : list;
      setDuplicateIds(computeDuplicateIds(visible));
    },
    [restrictCust, computeDuplicateIds, accessVersion]
  );

  const getTextFilterValue = useCallback(
    (row, key) => {
      if (!row) return EMPTY_VALUE;
      if (key === "cust_code") {
        const normalised = normalizeCustCode(row[key], { strict: true });
        return normalised || EMPTY_VALUE;
      }
      if (key === "ils_update_enabled") {
        return row?.[key] === false ? "Hold" : "On";
      }
      const raw = row?.[key];
      if (raw === null || raw === undefined) return EMPTY_VALUE;
      const trimmed = String(raw).trim();
      return trimmed === "" ? EMPTY_VALUE : trimmed;
    },
    [normalizeCustCode],
  );

  const getDisplayLabel = useCallback(
    (row, key, token) => {
      if (token === EMPTY_VALUE) return "(Blank)";
      if (key === "cust_code") {
        return normalizeCustCode(row?.[key]) || row?.[key] || token;
      }
      if (key === "ils_update_enabled") {
        return row?.[key] === false ? "Hold" : "On";
      }
      const raw = row?.[key];
      const trimmed = String(raw ?? "").trim();
      return trimmed || token;
    },
    [normalizeCustCode],
  );

  const buildFilterOptionsForRows = useCallback(
    (list = []) => {
      const next = {};
      TEXT_COLUMNS.forEach((key) => {
        const entries = new Map();
        list.forEach((row) => {
          const token = getTextFilterValue(row, key);
          if (!entries.has(token)) {
            const label = getDisplayLabel(row, key, token);
            entries.set(token, label || (token === EMPTY_VALUE ? "(Blank)" : token));
          }
        });
        const options = Array.from(entries.entries()).map(([value, label]) => ({
          value,
          label: value === EMPTY_VALUE ? "(Blank)" : label,
        }));
        options.sort((a, b) => a.label.localeCompare(b.label));
        if (MULTI_FILTER_KEYS.has(key)) {
          const blankIndex = options.findIndex((option) => option.value === EMPTY_VALUE);
          if (blankIndex > 0) {
            const [blankOption] = options.splice(blankIndex, 1);
            options.unshift(blankOption);
          }
        }
        next[key] = options;
      });
      return next;
    },
    [getTextFilterValue, getDisplayLabel],
  );

  useEffect(() => {
    (async () => {
      try {
        const response = await fetch(`${API_BASE}/customers`);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.ok) throw new Error(payload?.error || "Failed to load customers.");
        const meta = buildCustomerMetadata(payload.rows || []);
        setCustomerOptions(meta.options);
        aliasMapRef.current = meta.aliasMap;
        primaryMapRef.current = meta.primaryMap;
        setRows((prev) =>
          ensureRowIds(
            prev.map((row) => ({
              ...row,
              cust_code: normalizeCustCode(row.cust_code),
            })),
          ),
        );
      } catch (err) {
        console.warn("load customers failed", err);
      }
    })();
  }, [normalizeCustCode]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const response = await fetch(`${API_BASE}/mrp/products`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || "Failed to load products.");
      }
      const fetchedRows = Array.isArray(payload.rows) ? payload.rows : [];
      const hydrated = withDefaultFields(fetchedRows).map((row) => ({
        ...row,
        id: getProductId(row),
        cust_code: normalizeCustCode(row.cust_code ?? row.custCode),
        ils_update_enabled: row?.ils_update_enabled === false ? false : true,
      }));
      const normalised = ensureRowIds(hydrated);
      setRows(normalised);
      refreshDuplicates(normalised);
      setHighlightCells(new Map());
      const snap = rowsSnapshot(normalised);
      setSavedSnapshot(snap);
      setHasUnsaved(false);
      mirrorDirtyToStorage(false);
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Failed to load products.");
    } finally {
      setLoading(false);
    }
  }, [normalizeCustCode, refreshDuplicates]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    setFilterOptions(buildFilterOptionsForRows(accessFilteredRows));
  }, [accessFilteredRows, buildFilterOptionsForRows]);

  useEffect(() => {
    setPage(1);
  }, [filters]);

  useEffect(() => {
    const snap = rowsSnapshot(rows);
    const dirty = snap !== savedSnapshot;
    setHasUnsaved(dirty);
    mirrorDirtyToStorage(dirty);
  }, [rows, savedSnapshot]);

  useEffect(() => {
    const handler = (event) => {
      if (!hasUnsaved) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsaved]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_WIDTHS_KEY, JSON.stringify(widths));
    } catch {
      /* ignore */
    }
  }, [widths]);

  const updateCell = (rowId, key, value) => {
    setRows((prev) => {
      const next = prev.map((row) => (row._localId === rowId ? { ...row, [key]: value } : row));
      refreshDuplicates(next);
      return next;
    });
    setHighlightCells((prev) => {
      if (!prev.size) return prev;
      if (!prev.has(rowId)) return prev;
      const nextMap = new Map(prev);
      const columns = new Set(nextMap.get(rowId));
      columns.delete(key);
      if (columns.size > 0) nextMap.set(rowId, columns);
      else nextMap.delete(rowId);
      return nextMap;
    });
  };

  const addRow = () => {
    const newRow = ensureRowIds([{ ...EMPTY_ROW }])[0];
    if (restrictCust && activeCustTokens.length) {
      newRow.cust_code = activeCustTokens[0];
    }
    setRows((prev) => {
      const next = [newRow, ...prev];
      refreshDuplicates(next);
      return next;
    });
  };

  const onToggleSelect = (rowId, checked) => {
    if (rowId === null || rowId === undefined) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const onToggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    const allIds = new Set(
      filteredRows
        .map((row) => getProductId(row))
        .filter((value) => value !== null && value !== undefined)
    );
    setSelectedIds(allIds);
  };

  const selectedProductIds = useMemo(() => Array.from(selectedIds), [selectedIds]);
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIds.has(getProductId(row))),
    [rows, selectedIds],
  );
  const allSelectedEnabled = useMemo(
    () => selectedRows.length > 0 && selectedRows.every((row) => row?.ils_update_enabled !== false),
    [selectedRows],
  );

  const onBulkToggleIlsUpdate = useCallback(async () => {
    if (!isAdmin || bulkUpdating) return;
    if (selectedProductIds.length === 0) return;
    const setTo = allSelectedEnabled ? false : true;
    const label = setTo ? "On" : "Hold";
    const confirmed = window.confirm(
      `Set iLS Update to ${label} for ${selectedProductIds.length} selected products?`,
    );
    if (!confirmed) return;
    setBulkUpdating(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const response = await fetch(`${API_BASE}${ILS_UPDATE_ENDPOINT}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ ids: selectedProductIds, setTo }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || "Failed to update iLS Update.");
      }
      const selectedSet = new Set(selectedIds);
      setRows((prev) =>
        prev.map((row) =>
          selectedSet.has(getProductId(row))
            ? {
                ...row,
                ils_update_enabled: setTo,
              }
            : row,
        ),
      );
      setSelectedIds(new Set());
      setInfo("iLS Update updated.");
      setTimeout(() => setInfo(""), 3000);
    } catch (err) {
      setError(err.message || "Failed to update iLS Update.");
    } finally {
      setBulkUpdating(false);
    }
  }, [isAdmin, bulkUpdating, selectedProductIds, allSelectedEnabled, selectedIds, me?.email, me?.role]);

  const deleteSelected = () => {
    if (selectedIds.size === 0) return;
    const selectedLocalIds = new Set(
      rows
        .filter((row) => selectedIds.has(getProductId(row)))
        .map((row) => row._localId)
        .filter(Boolean),
    );
    setRows((prev) => {
      const next = prev.filter((row) => !selectedIds.has(getProductId(row)));
      refreshDuplicates(next);
      return next;
    });
    setHighlightCells((prev) => {
      if (!prev.size) return prev;
      let mutated = false;
      const next = new Map(prev);
      selectedLocalIds.forEach((id) => {
        if (next.has(id)) {
          mutated = true;
          next.delete(id);
        }
      });
      return mutated ? next : prev;
    });
    setSelectedIds(new Set());
  };

  const sanitizeRowsForSave = () =>
    rows.map(({ _localId, ...rest }) => ({
      ...rest,
      cust_code: normalizeCustCode(rest.cust_code, { strict: true }),
      weight_lbs: parseNumber(rest.weight_lbs),
      length_in: parseNumber(rest.length_in),
      width_in: parseNumber(rest.width_in),
      height_in: parseNumber(rest.height_in),
      cft: parseNumber(rest.cft),
      weight_kgs: parseNumber(rest.weight_kgs),
      pcs_per_box: parseNumber(rest.pcs_per_box),
      boxes_per_plt: parseNumber(rest.boxes_per_plt),
    }));

  const validateRows = useCallback(() => {
    const sourceRows = restrictCust ? accessFilteredRows : rows;
    const missingCust = [];
    const missingProduct = [];
    for (const row of sourceRows) {
      const normalized = normalizeCustCode(row.cust_code, { strict: true });
      const productCode = String(row.product_code ?? "").trim();
      if (!normalized) missingCust.push(row._localId);
      if (!productCode) missingProduct.push(row._localId);
    }
    if (missingCust.length > 0) {
      setDuplicateIds(new Set(missingCust));
      return "Cust Code is required.";
    }
    if (missingProduct.length > 0) {
      setDuplicateIds(new Set(missingProduct));
      return "Product Code is required.";
    }
    const duplicates = computeDuplicateIds(sourceRows);
    setDuplicateIds(duplicates);
    if (duplicates.size > 0) return "Duplicated items!";
    return "";
  }, [rows, accessFilteredRows, restrictCust, normalizeCustCode, computeDuplicateIds]);

  const onSave = async () => {
    setError("");
    setInfo("");
    const validationMessage = validateRows();
    if (validationMessage) {
      setError(validationMessage);
      try {
        window.alert(validationMessage);
      } catch {
        /* noop */
      }
      return;
    }

    setSaving(true);
    try {
      const payload = sanitizeRowsForSave();
      const response = await fetch(`${API_BASE}/mrp/products`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rows: payload }),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result?.ok) {
        throw new Error(result?.error || "Save failed.");
      }
      await loadRows();
      const warningText = Array.isArray(result?.warnings)
        ? result.warnings.filter(Boolean).join(" ")
        : String(result?.warning || "").trim();
      setInfo(warningText ? `Saved successfully. ${warningText}` : "Saved successfully.");
      setTimeout(() => setInfo(""), 3000);
    } catch (err) {
      setError(err.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const onExport = () => {
    const header = COLUMN_CONFIG.map((col) => col.label);
    const dataRows = rows.map((row) =>
      COLUMN_CONFIG.map((col) => {
        const value = row[col.key];
        if (col.key === "ils_update_enabled") {
          return value === false ? "Hold" : "On";
        }
        return value === null || value === undefined ? "" : value;
      })
    );
    const worksheet = XLSX.utils.aoa_to_sheet([header, ...dataRows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "MRP Products");
    XLSX.writeFile(workbook, "MRP Products.xlsx");
  };

  const fileInputRef = useRef(null);

  const onClickImport = () => {
    fileInputRef.current?.click();
  };

  const onImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setInfo("");
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!sheet) throw new Error("Worksheet not found.");
      const sheetRows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: false });
      if (sheetRows.length < 2) throw new Error("Worksheet is empty.");
      const [header, ...body] = sheetRows;
      const headerMap = COLUMN_CONFIG.reduce((acc, col) => {
        acc[col.label] = col.key;
        acc[normalizeHeaderLabel(col.label)] = col.key;
        return acc;
      }, {});
      const imported = body
        .filter((record) => record.some((cell) => cell !== undefined && cell !== ""))
        .map((record) => {
          const next = { ...EMPTY_ROW };
          header.forEach((label, idx) => {
            const key = headerMap[label] || headerMap[normalizeHeaderLabel(label)];
            if (!key) return;
            const cell = record[idx] ?? "";
            if (key === "cust_code") {
              next[key] = normalizeCustCode(cell, { strict: true });
            } else if (key === "product_code") {
              next[key] = String(cell || "").trim();
            } else {
              next[key] = cell;
            }
          });
          return next;
        });
      if (!imported.length) throw new Error("No rows were imported.");
      const invalidCust = imported.some((row) => !row.cust_code);
      if (invalidCust) {
        setError("Wrong Customer Code!!");
        setInfo("");
        try {
          window.alert("Wrong Customer Code!!");
        } catch {
          /* noop */
        }
        return;
      }
      const invalidProduct = imported.some((row) => !String(row.product_code ?? "").trim());
      if (invalidProduct) {
        setError("Please enter Product Code!!");
        setInfo("");
        try {
          window.alert("Please enter Product Code!!");
        } catch {
          /* noop */
        }
        return;
      }
      const seenKeys = new Set();
      let hasDuplicateInFile = false;
      imported.forEach((row) => {
        const key = buildCompositeKey(row.cust_code, row.product_code, row.supplier_code);
        if (!key) return;
        if (seenKeys.has(key)) hasDuplicateInFile = true;
        else seenKeys.add(key);
      });
      if (hasDuplicateInFile) {
        setError("Duplicated items!");
        setInfo("");
        try {
          window.alert("Duplicated items!");
        } catch {
          /* noop */
        }
        return;
      }
      const prepared = withDefaultFields(imported).map((row) => ({
        ...row,
        cust_code: normalizeCustCode(row.cust_code, { strict: true }),
        product_code: String(row.product_code ?? "").trim(),
      }));
      const existingKeyMap = new Map();
      rows.forEach((row) => {
        const cust = normalizeCustCode(row.cust_code, { strict: true });
        const product = String(row.product_code ?? "").trim();
        const supplier = String(row.supplier_code ?? "").trim();
        const key = buildCompositeKey(cust, product, supplier);
        if (key) existingKeyMap.set(key, row);
      });
      const highlightMap = new Map();
      const newRows = [];
      prepared.forEach((row) => {
        const key = buildCompositeKey(row.cust_code, row.product_code, row.supplier_code);
        if (!key) return;
        const existingRow = existingKeyMap.get(key);
        if (existingRow) {
          const updatedRow = { ...existingRow };
          const changedColumns = new Set();
          COLUMN_KEYS.forEach((colKey) => {
            const incoming = row[colKey] ?? "";
            const current = existingRow[colKey] ?? "";
            if (String(incoming ?? "") !== String(current ?? "")) {
              updatedRow[colKey] = incoming;
              changedColumns.add(colKey);
            }
          });
          if (changedColumns.size > 0) {
            highlightMap.set(updatedRow._localId, changedColumns);
            existingKeyMap.set(key, updatedRow);
          } else {
            existingKeyMap.set(key, existingRow);
          }
        } else {
          const newRow = ensureRowIds([{ ...row }])[0];
          const changedColumns = new Set(COLUMN_KEYS);
          highlightMap.set(newRow._localId, changedColumns);
          newRows.push(newRow);
          existingKeyMap.set(key, newRow);
        }
      });
      const mergedRows = rows.map((row) => {
        const cust = normalizeCustCode(row.cust_code, { strict: true });
        const product = String(row.product_code ?? "").trim();
        const supplier = String(row.supplier_code ?? "").trim();
        const key = buildCompositeKey(cust, product, supplier);
        const replacement = key ? existingKeyMap.get(key) : null;
        return replacement ? replacement : row;
      });
      const finalRows = ensureRowIds([...mergedRows, ...newRows]);
      const highlightSnapshot = new Map();
      highlightMap.forEach((cols, id) => {
        if (cols.size > 0) highlightSnapshot.set(id, new Set(cols));
      });
      setRows(finalRows);
      setHighlightCells(highlightSnapshot);
      refreshDuplicates(finalRows);
      setError("");
      setInfo("New Value with Blue!");
      setSelectedIds(new Set());
    } catch (err) {
      setError(err.message || "Import failed.");
    }
  };

  const filteredRows = useMemo(() => {
    if (!accessFilteredRows.length) return [];
    return accessFilteredRows.filter((row) => {
      for (const key of TEXT_COLUMNS) {
        const selections = filters[key] || [];
        if (selections.length > 0) {
          const token = getTextFilterValue(row, key);
          if (!selections.includes(token)) return false;
        }
      }
      for (const key of NUMBER_COLUMNS) {
        const filterState = filters[key] || { operator: "", value: "" };
        const { operator, value } = filterState;
        if (operator && value !== "") {
          const target = Number(value);
          if (Number.isNaN(target)) continue;
          const numericRowValue = Number(row[key]);
          if (Number.isNaN(numericRowValue)) return false;
          if (operator === "=" && numericRowValue !== target) return false;
          if (operator === ">" && numericRowValue <= target) return false;
          if (operator === "<" && numericRowValue >= target) return false;
        }
      }
      return true;
    });
  }, [accessFilteredRows, filters, getTextFilterValue]);

  const sortedRows = useMemo(() => {
    if (!filteredRows.length) return [];
    if (!sortKey || sortDirection === 0) return filteredRows;
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      const leftRaw = a?.[sortKey];
      const rightRaw = b?.[sortKey];
      if (NUMBER_COLUMN_SET.has(sortKey)) {
        const leftNum = Number(leftRaw);
        const rightNum = Number(rightRaw);
        const aNum = Number.isFinite(leftNum) ? leftNum : Number.NEGATIVE_INFINITY;
        const bNum = Number.isFinite(rightNum) ? rightNum : Number.NEGATIVE_INFINITY;
        if (aNum !== bNum) {
          return sortDirection === 1 ? aNum - bNum : bNum - aNum;
        }
      }
      const leftStr = leftRaw === null || leftRaw === undefined ? '' : String(leftRaw).toLowerCase();
      const rightStr = rightRaw === null || rightRaw === undefined ? '' : String(rightRaw).toLowerCase();
      if (leftStr === rightStr) return 0;
      return sortDirection === 1 ? leftStr.localeCompare(rightStr) : rightStr.localeCompare(leftStr);
    });
    return copy;
  }, [filteredRows, sortKey, sortDirection]);

  const totalFiltered = sortedRows.length;
  const pageSizeNumber = pageSize === "All" ? (totalFiltered || 1) : pageSize;
  const totalPages = pageSize === "All" ? 1 : Math.max(1, Math.ceil(totalFiltered / pageSizeNumber));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedRows = useMemo(() => {
    if (pageSize === "All") return sortedRows;
    const start = (page - 1) * pageSizeNumber;
    return sortedRows.slice(start, start + pageSizeNumber);
  }, [sortedRows, page, pageSize, pageSizeNumber]);

  const displayStart = totalFiltered === 0 ? 0 : pageSize === "All" ? 1 : (page - 1) * pageSizeNumber + 1;
  const displayEnd =
    totalFiltered === 0 ? 0 : pageSize === "All" ? totalFiltered : Math.min(totalFiltered, (page - 1) * pageSizeNumber + pageSizeNumber);
  const disablePrev = page <= 1 || totalFiltered === 0;
  const disableNext = page >= totalPages || totalFiltered === 0;
  const pageSizeSelectValue = pageSize === "All" ? "All" : String(pageSize);

  const handlePageSizeChange = (value) => {
    const parsed = value === "All" ? "All" : Number(value);
    setPageSize(parsed);
    setPage(1);
  };

  const goToPage = (nextPage) => {
    const target = Math.min(Math.max(1, nextPage), totalPages);
    setPage(target);
  };

  const resetFilters = () => {
    setFilters(createInitialFilters());
    setPage(1);
  };
  const handleSort = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection(1);
      setPage(1);
      return;
    }
    const nextDirection = sortDirection === 1 ? -1 : sortDirection === -1 ? 0 : 1;
    setSortDirection(nextDirection);
    if (nextDirection === 0) {
      setSortKey("");
    }
  };

  const renderSortIcon = (key) => {
    if (sortKey !== key || sortDirection === 0) {
      return (
        <svg className="h-3.5 w-3.5 text-slate-400" viewBox="0 0 20 20" fill="currentColor">
          <path d="M7 7l3-3 3 3H7zM13 13l-3 3-3-3h6z" />
        </svg>
      );
    }
    if (sortDirection === 1) {
      return (
        <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
          <path d="M6 12l4-4 4 4H6z" />
        </svg>
      );
    }
    return (
      <svg className="h-3.5 w-3.5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
        <path d="M6 8l4 4 4-4H6z" />
      </svg>
    );
  };


  const onDragging = useCallback((event) => {
    const { key, startX, startW } = dragRef.current;
    if (!key) return;
    const dx = event.clientX - startX;
    const nextWidth = Math.max(100, startW + dx);
    setWidths((prev) => ({ ...prev, [key]: nextWidth }));
  }, []);

  const onDragEnd = useCallback(() => {
    window.removeEventListener("mousemove", onDragging);
    window.removeEventListener("mouseup", onDragEnd);
    dragRef.current = { key: "", startX: 0, startW: 0 };
  }, [onDragging]);

  const onDragStart = (event, key) => {
    dragRef.current = { key, startX: event.clientX, startW: widths[key] || 160 };
    window.addEventListener("mousemove", onDragging);
    window.addEventListener("mouseup", onDragEnd);
    event.preventDefault();
  };

  useEffect(() => {
    return () => {
      window.removeEventListener("mousemove", onDragging);
      window.removeEventListener("mouseup", onDragEnd);
    };
  }, [onDragging, onDragEnd]);

  const handleSyncProducts = async () => {
    setSyncing(true);
    setSyncError("");
    setSyncSummary(null);
    try {
      const me = getCurrentUser();
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const response = await fetch(`${API_BASE}/api/admin/mrp/sync-products`, {
        method: "POST",
        headers,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = response.status === 409 ? "Sync already running." : data?.error || "Sync failed.";
        throw new Error(message);
      }
      if (!data?.ok) {
        throw new Error(data?.error || "Sync failed.");
      }
      setSyncSummary(data);
      if (data?.finished_at) {
        setLastSyncAt(data.finished_at);
      }
      await loadRows();
    } catch (err) {
      console.error(err);
      setSyncError(err.message || "Sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const formatUtcTimestamp = (value) => {
    if (!value) return "";
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    const pad = (num) => String(num).padStart(2, "0");
    return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())} ${pad(
      dt.getUTCHours()
    )}:${pad(dt.getUTCMinutes())} UTC`;
  };

  const selectableRows = filteredRows.filter((row) => getProductId(row) !== null && getProductId(row) !== undefined);
  const selectableCount = selectableRows.length;
  const selectedCount = selectedIds.size;
  const allSelected = selectableCount > 0 && selectedCount === selectableCount;
  const someSelected = selectedCount > 0 && selectedCount < selectableCount;

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={onImport}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold text-slate-900">MRP Products</h1>
          <span className="text-sm text-slate-600">
            {loading ? "Loading..." : `${totalFiltered.toLocaleString()} rows`}
          </span>
          {info && <span className="text-sm text-emerald-600">{info}</span>}
          {error && <span className="text-sm text-red-600">{error}</span>}
        </div>
        <UserAccessSummary />
      </div>

      {syncError && (
        <div className="flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{syncError}</span>
          <button
            type="button"
            onClick={() => setSyncError("")}
            className="text-xs text-red-600 hover:text-red-800"
          >
            Dismiss
          </button>
        </div>
      )}

      {syncSummary && (
        <div className="flex items-start justify-between gap-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          <div className="space-y-1">
            <div>
              <span className="font-semibold">Wayli:</span>{" "}
              {`updated ${syncSummary?.wayli?.vessels_updated ?? 0}, inserted ${syncSummary?.wayli?.vessels_inserted ?? 0}, skipped ${syncSummary?.wayli?.vessels_skipped ?? 0}, containers replaced ${syncSummary?.wayli?.containers_replaced ?? 0}`}
            </div>
            <div>
              <span className="font-semibold">MRP:</span>{" "}
              {`suggestions ${syncSummary?.mrp?.suggestions ?? 0}, warnings ${syncSummary?.mrp?.warnings ?? 0}, errors ${syncSummary?.mrp?.errors ?? 0}`}
            </div>
            <div>
              <span className="font-semibold">Finished at:</span> {syncSummary?.finished_at || "-"}
            </div>
            <div>
              <a href="/admin/ingest-warnings" className="font-semibold text-emerald-700 underline">
                View Ingest Warnings
              </a>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSyncSummary(null)}
            className="text-xs text-emerald-700 hover:text-emerald-900"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50"
          >
            Add Row
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || loading || !hasUnsaved}
            className="rounded border border-blue-500 bg-blue-600 px-3 py-2 font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saving ? "SavingÃ¢â€šÂ¬Ã‚Â¦" : "Save"}
          </button>
          <button
            type="button"
            onClick={onExport}
            className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50"
          >
            Export Excel
          </button>
          <button
            type="button"
            onClick={onClickImport}
            className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50"
          >
            Import Excel
          </button>
          <button
            type="button"
            onClick={deleteSelected}
            disabled={selectedIds.size === 0}
            className="rounded border border-red-500 bg-red-600 px-3 py-2 font-medium text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            Delete
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={onBulkToggleIlsUpdate}
              disabled={!isAdmin || selectedCount === 0 || bulkUpdating}
              className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {bulkUpdating ? "Updating..." : "iLS Hold/Unhold"}
            </button>
          )}
          <button
            type="button"
            onClick={loadRows}
            className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50"
            disabled={loading}
          >
            Refresh
          </button>
          <div className="flex items-center gap-2">
            {lastSyncAt && (
              <div className="text-xs text-slate-600">
                <span className="font-semibold">Last Sync:</span>{" "}
                {formatUtcTimestamp(lastSyncAt)}
              </div>
            )}
            <button
              type="button"
              onClick={handleSyncProducts}
              disabled={syncing}
              className="rounded border border-slate-300 bg-white px-3 py-2 shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {syncing ? "Syncing..." : "Sync Products"}
            </button>
            <span
              className="inline-flex items-center text-slate-500 cursor-help"
              title="Manually sync product data from Wayli. This may take several minutes."
              aria-label="Manually sync product data from Wayli. This may take several minutes."
            >
              <Info size={16} />
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-600">
          <div className="flex items-center gap-1">
            <span>Rows:</span>
            <select
              className="rounded border border-slate-300 bg-white px-2 py-1"
              value={pageSizeSelectValue}
              onChange={(event) => handlePageSizeChange(event.target.value)}
            >
              {PAGE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => goToPage(1)}
              disabled={disablePrev}
              className="h-8 w-8 grid place-items-center rounded border border-slate-300 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {"<<"}
            </button>
            <button
              type="button"
              onClick={() => goToPage(page - 1)}
              disabled={disablePrev}
              className="h-8 w-8 grid place-items-center rounded border border-slate-300 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {"<"}
            </button>
            <span className="px-1 text-slate-700">
              {totalFiltered === 0 ? "0 / 0" : `${page} / ${totalPages}`}
            </span>
            <button
              type="button"
              onClick={() => goToPage(page + 1)}
              disabled={disableNext}
              className="h-8 w-8 grid place-items-center rounded border border-slate-300 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {">"}
            </button>
            <button
              type="button"
              onClick={() => goToPage(totalPages)}
              disabled={disableNext}
              className="h-8 w-8 grid place-items-center rounded border border-slate-300 bg-white text-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {">>"}
            </button>
          </div>
          <span className="text-slate-500">
            {totalFiltered === 0 ? "0 of 0" : `${displayStart} - ${displayEnd} of ${totalFiltered}`}
          </span>
        </div>
      </div>

      <div className="relative max-h-[calc(100vh-180px)] min-h-[440px] overflow-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full border-collapse">
          <thead>
            <tr>
              <th
                className="sticky top-0 z-20 bg-white border-b px-2 py-2 text-center"
                style={{ width: CHECKBOX_COLUMN_WIDTH, minWidth: CHECKBOX_COLUMN_WIDTH }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={(event) => onToggleSelectAll(event.target.checked)}
                />
              </th>
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width, minWidth: column.width }}
                  className="sticky top-0 z-20 bg-white border-b px-3 py-2 text-left text-sm font-semibold relative select-none"
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleSort(column.key)}
                      className="flex items-center gap-1 text-slate-700 focus:outline-none"
                    >
                      {column.key === "ils_update_enabled" ? (
                        <span
                          className="inline-flex items-center gap-1"
                          title="MRP Inventory will call iLS if set 'On'."
                          aria-label="MRP Inventory will call iLS if set 'On'."
                        >
                          <span>{column.label}</span>
                          <Info size={14} className="text-slate-400" />
                        </span>
                      ) : (
                        <span>{column.label}</span>
                      )}
                      {renderSortIcon(column.key)}
                    </button>
                    <span
                      onMouseDown={(event) => onDragStart(event, column.key)}
                      className="absolute right-0 top-0 h-full w-1 cursor-col-resize"
                      title="Drag to resize"
                      style={{ userSelect: "none" }}
                    />
                  </div>
                </th>
              ))}
            </tr>
            <tr className="sticky top-[42px] bg-white">
              <th style={{ width: CHECKBOX_COLUMN_WIDTH, minWidth: CHECKBOX_COLUMN_WIDTH }} className="bg-white border-b px-2 py-1">
                <button
                  type="button"
                  onClick={resetFilters}
                  title="Reset filters"
                  aria-label="Reset filters"
                  className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1"
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 12a9 9 0 1 1-3-6.7L21 7" />
                    <path d="M21 3v4h-4" />
                  </svg>
                </button>
              </th>
              {visibleColumns.map((column) => {
                const widthStyle = { width: column.width, minWidth: column.width };
                const cellClass = "bg-emerald-50 border-b px-2 py-1";
                if (NUMBER_COLUMN_SET.has(column.key)) {
                  const numericFilter = filters[column.key] || { operator: "", value: "" };
                  return (
                    <th key={column.key} style={widthStyle} className={cellClass}>
                      <NumberFilter
                        value={numericFilter}
                        onChange={(next) => setFilters((prev) => ({ ...prev, [column.key]: next }))}
                      />
                    </th>
                  );
                }
                const selectedValues = filters[column.key] || [];
                const options = filterOptions[column.key] || [];
                return (
                  <th key={column.key} style={widthStyle} className={cellClass}>
                    <MultiSelectDropdown
                      value={selectedValues}
                      options={options}
                      onChange={(next) => setFilters((prev) => ({ ...prev, [column.key]: next }))}
                      placeholder="All"
                      buttonClassName="text-xs"
                      showSelectAll={MULTI_FILTER_KEYS.has(column.key)}
                      showSearch={MULTI_FILTER_KEYS.has(column.key)}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="px-3 py-6 text-center text-sm text-slate-500">
                  Loading productsÃ¢â€šÂ¬Ã‚Â¦
                </td>
              </tr>
            ) : accessFilteredRows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="px-3 py-6 text-center text-sm text-slate-500">
                  No data available for your access.
                </td>
              </tr>
            ) : (
              paginatedRows.map((row) => {
                const isDuplicate = duplicateIds.has(row._localId);
                const changedCols = highlightCells.get(row._localId);
                const productId = getProductId(row);
                const hasProductId = productId !== null && productId !== undefined;
                return (
                  <tr key={row._localId} className="border-b">
                    <td
                      className="px-2 py-2 align-top text-center"
                      style={{ width: CHECKBOX_COLUMN_WIDTH, minWidth: CHECKBOX_COLUMN_WIDTH }}
                    >
                      <input
                        type="checkbox"
                        checked={hasProductId ? selectedIds.has(productId) : false}
                        onChange={(event) => onToggleSelect(productId, event.target.checked)}
                        disabled={!hasProductId}
                        title={!hasProductId ? "Missing product id" : ""}
                      />
                    </td>
                    {visibleColumns.map((column) => {
                      const isChanged = !!changedCols && changedCols.has(column.key);
                      const cellHighlightClass = isDuplicate
                        ? "bg-red-50"
                        : isChanged
                        ? "bg-blue-50"
                        : "";
                      return (
                        <td
                          key={column.key}
                          className={`px-3 py-2 text-sm text-slate-700 align-top ${cellHighlightClass}`}
                          style={{ width: column.width, minWidth: column.width }}
                        >
                          <EditableCell
                            column={column}
                            value={row[column.key]}
                            onChange={(value) => updateCell(row._localId, column.key, value)}
                            isDuplicate={isDuplicate}
                            isChanged={isChanged}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditableCell({ column, value, onChange, isDuplicate = false, isChanged = false }) {
  const baseClass = "w-full rounded px-2 py-1 text-sm border";
  const highlightClass = isDuplicate
    ? " border-red-500 bg-red-50"
    : isChanged
    ? " border-blue-500 bg-blue-50"
    : " border-slate-300";
  const finalClassName = `${baseClass}${highlightClass}`;
  if (column.type === "readonly") {
    const displayValue = column.key === "ils_update_enabled" ? (value === false ? "Hold" : "On") : value;
    return <span className="text-sm text-slate-700">{displayValue ?? ""}</span>;
  }

  if (column.type === "number") {
    return (
      <input
        type="number"
        className={finalClassName}
        value={value ?? ""}
        min={column.key === "plt_cube" ? 0 : undefined}
        step={column.key === "plt_cube" ? "0.01" : undefined}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  if (column.type === "select") {
    const placeholder = column.placeholder || "-- Select --";
    const options = column.options || [];
    const currentValue = value ?? "";
    const hasCurrent = currentValue === "" ? true : options.some((option) => option.value === currentValue);
    const renderOptions =
      hasCurrent || currentValue === ""
        ? options
        : [{ value: currentValue, label: `${currentValue} (legacy)`, disabled: true }, ...options];
    return (
      <select
        className={finalClassName}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>
        {renderOptions.map((option) => (
          <option key={option.value || option.label} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      type="text"
      className={finalClassName}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function NumberFilter({ value, onChange }) {
  const operator = value?.operator ?? "";
  const val = value?.value ?? "";

  const handleOperatorChange = (nextOperator) => {
    onChange?.({ operator: nextOperator, value: val });
  };

  const handleValueChange = (nextValue) => {
    onChange?.({ operator, value: nextValue });
  };

  return (
    <div className="flex items-center gap-1">
      <select
        className="w-16 rounded border border-slate-300 bg-white px-2 py-1 text-xs"
        value={operator}
        onChange={(event) => handleOperatorChange(event.target.value)}
      >
        <option value="">All</option>
        <option value="=">=</option>
        <option value=">">&gt;</option>
        <option value="<">&lt;</option>
      </select>
      <input
        type="number"
        className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
        value={val}
        onChange={(event) => handleValueChange(event.target.value)}
      />
    </div>
  );
}

function MultiSelectDropdown({
  value = [],
  options = [],
  onChange,
  placeholder = "All",
  buttonClassName = "",
  showSelectAll = false,
  showSearch = false,
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  const labelMap = useMemo(() => {
    const map = new Map();
    options.forEach((option) => {
      map.set(option.value, option.label ?? option.value ?? "");
    });
    return map;
  }, [options]);

  const labels = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return value.map((token) => {
      if (token === EMPTY_VALUE) return "(Blank)";
      return labelMap.get(token) ?? token;
    });
  }, [value, labelMap]);

  const summary = useMemo(() => {
    if (!value || value.length === 0) return placeholder;
    if (labels.length === 1) return labels[0] || placeholder;
    if (labels.length === 2) return labels.join(", ");
    return `${labels.length} selected`;
  }, [labels, value, placeholder]);

  const optionOrder = useMemo(() => options.map((option) => option.value), [options]);
  const hasOptions = optionOrder.length > 0;
  const allSelected = hasOptions && optionOrder.every((optionValue) => value.includes(optionValue));
  const filteredOptions = useMemo(() => {
    if (!showSearch) return options;
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => {
      const label = String(option?.label ?? option?.value ?? "").toLowerCase();
      return label.includes(term);
    });
  }, [options, searchTerm, showSearch]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const toggleOption = (optionValue) => {
    const current = Array.isArray(value) ? [...value] : [];
    const idx = current.indexOf(optionValue);
    if (idx >= 0) current.splice(idx, 1);
    else current.push(optionValue);
    onChange?.(current);
  };

  const handleClear = () => {
    if (!value || value.length === 0) return;
    onChange?.([]);
  };

  const handleSelectAll = () => {
    if (!hasOptions || allSelected) return;
    onChange?.(optionOrder.slice());
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className={`flex w-full items-center justify-between gap-2 rounded border border-slate-300 bg-white px-2 py-1 text-left text-xs shadow-sm hover:border-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${buttonClassName}`}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate text-slate-700">{summary}</span>
        <svg
          className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M6 8l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 z-20 mt-1 min-w-[160px] rounded border border-slate-200 bg-white shadow-lg">
          {(showSelectAll || showSearch) && (
            <div className="sticky top-0 z-10 border-b border-slate-200 bg-white">
              <div className="flex items-center justify-between px-2 py-1.5 text-xs">
                {showSelectAll ? (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!hasOptions || allSelected}
                  >
                    Select All
                  </button>
                ) : (
                  <span />
                )}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleClear}
                    className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={!value || value.length === 0}
                  >
                    Clear
                  </button>
                  <button type="button" onClick={() => setOpen(false)} className="text-blue-600 hover:underline">
                    Close
                  </button>
                </div>
              </div>
              {showSearch && (
                <div className="border-t border-slate-200 px-2 py-2">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Search..."
                    className="w-full rounded border border-slate-300 px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          )}
          <div className="max-h-48 overflow-y-auto py-1">
            {filteredOptions.length ? (
              filteredOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-slate-700 hover:bg-slate-100"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-blue-600"
                    checked={Array.isArray(value) && value.includes(option.value)}
                    onChange={() => toggleOption(option.value)}
                  />
                  <span className="truncate">{option.label ?? option.value ?? ""}</span>
                </label>
              ))
            ) : (
              <div className="px-2 py-1.5 text-xs text-slate-500">No options found</div>
            )}
          </div>
          {!showSelectAll && !showSearch && (
            <div className="flex items-center justify-between border-t border-slate-200 px-2 py-1 text-xs">
              <button
                type="button"
                onClick={handleClear}
                className="text-blue-600 hover:underline disabled:cursor-not-allowed disabled:opacity-40"
                disabled={!value || value.length === 0}
              >
                Clear
              </button>
              <button type="button" onClick={() => setOpen(false)} className="text-blue-600 hover:underline">
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}



